import { useEffect, useRef } from 'react';

/**
 * Traps keyboard focus inside a container while `active` is true.
 * On activation, stores the previously focused element and moves focus
 * to the first focusable child. On deactivation, restores focus.
 */
export function useFocusTrap(active: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active || !containerRef.current) return;

    previousFocus.current = document.activeElement as HTMLElement;

    const focusableSelector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const focusable = containerRef.current.querySelectorAll<HTMLElement>(focusableSelector);
    if (focusable.length) focusable[0].focus();

    function trap(e: KeyboardEvent) {
      if (e.key !== 'Tab' || !containerRef.current) return;
      const nodes = containerRef.current.querySelectorAll<HTMLElement>(focusableSelector);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // Dispatch a custom event so the parent can handle closing
        containerRef.current?.dispatchEvent(new CustomEvent('escape-pressed', { bubbles: true }));
      }
    }

    document.addEventListener('keydown', trap);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', trap);
      document.removeEventListener('keydown', handleEscape);
      previousFocus.current?.focus();
    };
  }, [active]);

  return containerRef;
}
